import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { HeadingText50 } from '@modernhealth/vital-components';
import { useDispatch, useSelector } from 'react-redux';

import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import {
    EnterInsuranceInfoForm,
    InsuranceRelationshipOption,
    InsuranceMemberData,
    InsurancePlanData,
} from '../components/EnterInsuranceInfoForm';
import { SubmissionModalContainer } from '../components/SubmissionModalContainer';
import { useGetDependentRelationshipCodesQuery } from '../documents/GetRelationshipCode.generated';
import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { mapDataToInsuranceSubmission } from '../helpers/mapDataToInsuranceSubmission';
import { InvalidInsuranceErrorBox } from '../components/InvalidInsuranceErrorBox';
import { EmployerInsuranceNode } from 'src/generated/webapp_gql';
import { HpiEligibilityFlow } from 'src/shared/models/shared';
import { useHpiEligibilityFlow } from '../hooks/useHpiEligibilityFlow';
import { useHpiEligibilityFlowAnalytics } from '../analytics';
import { useCheckEligibilityMutation } from '../documents/CheckEligibility.generated';
import { useCreateOrUpdatePayerInformationMutation } from '../documents/CreateOrUpdatePayerInformation.generated';
import { captureException } from 'src/shared/helpers/captureException';

const FormBoundary = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'calc(500px + 2 * 1rem)', // 500px + 2x padding
    padding: '0 1rem',
    margin: '0 auto 3rem',
});

export enum EligibilityError {
    INVALID_INPUT = 'INVALID_INPUT',
    INVALID_DATE_RANGE = 'INVALID_DATE_RANGE',
    NO_COVERAGE = 'NO_COVERAGE',
}

type Props = RouteComponentProps<
    {},
    {},
    {
        employerInsurance?: EmployerInsuranceNode;
        session?: string;
        routedFromProfilePage?: boolean;
        hpiEligibilityFlow?: HpiEligibilityFlow;
    }
>;

export const EnterInsuranceInfoPage: React.FC<Props> = ({ location }) => {
    const [createOrUpdatePayerInformation] = useCreateOrUpdatePayerInformationMutation();
    const [checkEligibilityMutation] = useCheckEligibilityMutation();
    const [awaitingInsurance, setAwaitingInsurance] = useState(false);
    const [showInvalidInputError, setShowInvalidInputError] = useState(false);
    const { email } = useSelector(getUserInformation);
    const [relationshipOptions, setRelationshipOptions] = useState<
        InsuranceRelationshipOption[] | null
    >();
    const { t } = useTranslation();
    const { loading, data, error } = useGetDependentRelationshipCodesQuery();
    const dispatch = useDispatch<Dispatch>();

    const { employerInsurance, session } = location.state;

    const user = useSelector(getUserInformation);

    const routedFromProfilePage = location.state.routedFromProfilePage ?? false;
    const hpiEligibilityFlow = useHpiEligibilityFlow();
    const analytics = useHpiEligibilityFlowAnalytics(
        'enter-insurance-plan-information',
        hpiEligibilityFlow,
    );

    useEffect(() => {
        if (loading) {
            return;
        }

        if (error) {
            Sentry.setExtra(
                'Insurance form error',
                'Error retrieving dependent relationship codes from server',
            );
            Sentry.captureException(error);
            captureException(error, {
                'Insurance form error': 'Error retrieving dependent relationship codes from server',
            });
            toastNotificationsService.renderErrorToast(
                t('translation:notifications.generic_error'),
            );
            return;
        }

        if (data) {
            const onlyRelationshipOptions = data?.getRelationshipCodes
                ?.filter(item => item?.code && item?.description)
                .map(item => {
                    const relationship: InsuranceRelationshipOption = {
                        code: item?.code || '',
                        description: item?.description || '',
                    } as const;
                    return relationship;
                });
            if (!onlyRelationshipOptions) {
                return;
            }

            setRelationshipOptions(onlyRelationshipOptions);
        }
    }, [data, loading, error, t]);

    const shouldRedirect =
        hpiEligibilityFlow === HpiEligibilityFlow.Therapy
            ? !user.isCompanyHpiEnabled
            : !user.isCompanyPsychiatryEnabled;

    if (shouldRedirect) {
        return <Redirect to="/dashboard" />;
    }

    if (!employerInsurance) {
        return (
            <Redirect
                to={{
                    pathname: '/inquire-employer-insurance-coverage',
                    state: { session, routedFromProfilePage, hpiEligibilityFlow },
                }}
            />
        );
    }

    if (loading) {
        return <CenteredSpinner />;
    }

    if (error || !relationshipOptions) {
        return null;
    }

    const handleEligibilityRedirect = async ({
        isCreatePayerSuccess,
        isEligibilityCheckSuccess,
        ineligibleReason = '',
    }) => {
        if (!isCreatePayerSuccess) {
            toastNotificationsService.renderErrorToast(
                t('insuranceForm:insurance_form.submission_error'),
            );
            Sentry.captureException(
                `Failed to createOrUpdatePayerInformation for member ${user.uuid}`,
            );
            captureException(new Error('Failed to createOrUpdatePayerInformation for member'), {
                member: user.uuid,
            });

            return navigationService.navigate('/insurance-error', {
                routedFromProfilePage,
                hpiEligibilityFlow,
            });
        }

        if (ineligibleReason.length > 0) {
            switch (ineligibleReason) {
                case EligibilityError.INVALID_DATE_RANGE:
                    return navigationService.navigate('/insurance-inactive', {
                        routedFromProfilePage,
                        hpiEligibilityFlow,
                    });
                case EligibilityError.NO_COVERAGE:
                    return navigationService.navigate('/insurance-ineligible', {
                        routedFromProfilePage,
                        hpiEligibilityFlow,
                    });
                case EligibilityError.INVALID_INPUT:
                    analytics.track('Enter Insurance Plan Information: Invalid Input');
                    setAwaitingInsurance(false);
                    return setShowInvalidInputError(true);
                default:
                    return navigationService.navigate('/insurance-error', {
                        routedFromProfilePage,
                        hpiEligibilityFlow,
                    });
            }
        }

        if (isEligibilityCheckSuccess) {
            await dispatch.user.fetchUserInfo(); // update the member's latest insurance and eligibility info to match BE state
            await dispatch.videoSessions.fetchSessions(); // update the redux store session status to match BE - race condition
            return navigationService.navigate('/insurance-approved', {
                session,
                routedFromProfilePage,
                hpiEligibilityFlow,
            });
        }

        return navigationService.navigate('/insurance-error', {
            routedFromProfilePage,
            hpiEligibilityFlow,
        });
    };

    const handleFormSubmit = async (submissionData: InsurancePlanData & InsuranceMemberData) => {
        analytics.trackClick('Enter Insurance Plan Information: Form submit');

        const submitInsuranceVariables = mapDataToInsuranceSubmission({
            submissionData,
            employerInsuranceId: employerInsurance.employerInsuranceId,
            email: email ?? '',
        });

        try {
            setShowInvalidInputError(false);
            setAwaitingInsurance(true);
            const { data: payerInfoData } = await createOrUpdatePayerInformation({
                variables: { payerInput: submitInsuranceVariables.payerInput },
            });
            const isCreatePayerSuccess = payerInfoData?.createOrUpdatePayerInformation?.ok;

            const { data: eligibilityCheckData } = await checkEligibilityMutation({
                variables: {
                    checkEligibilityInput: submitInsuranceVariables.checkEligibilityInput,
                },
            });
            const isEligibilityCheckSuccess = eligibilityCheckData?.checkEligibility?.ok;
            const ineligibleReason =
                eligibilityCheckData?.checkEligibility?.ineligibleReason || undefined;

            handleEligibilityRedirect({
                isCreatePayerSuccess,
                isEligibilityCheckSuccess,
                ineligibleReason,
            });
        } catch (err) {
            toastNotificationsService.renderErrorToast(
                t('insuranceForm:insurance_form.submission_error'),
            );
            Sentry.withScope(scope => {
                scope.setExtras({
                    error_info: 'Failed submitting member insurance plan info',
                    submitInsuranceVariables,
                });
                Sentry.captureException(err);
            });
            captureException(err, {
                error_info: 'Failed submitting member insurance plan info',
                submitInsuranceVariables,
            });
            setAwaitingInsurance(false);
        }
    };

    return (
        <FormBoundary>
            <HeadingText50 marginBottom={30}>
                {t('insuranceForm:insurance_form.page_title')}
            </HeadingText50>

            {showInvalidInputError && <InvalidInsuranceErrorBox />}

            <EnterInsuranceInfoForm
                relationshipOptions={relationshipOptions}
                onFormSubmit={handleFormSubmit}
                onCancel={() => {
                    analytics.trackClick('Enter Insurance Plan Information: Back to home');
                    navigationService.replace('/dashboard');
                }}
                isLoading={awaitingInsurance}
            />
            {awaitingInsurance && <SubmissionModalContainer />}
        </FormBoundary>
    );
};
